import React, { useEffect, useState, useCallback } from 'react'
import { Header } from './components/Header'
import { cn, enqueueNotification } from 'Utils/Helpers'
import { ChannelRow, Typography } from 'procyon-ui'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { useHistory, useParams } from 'react-router'
import { useChannelBrowser } from './providers/hooks'
import { ChannelBrowserContext } from './providers/BrowserContext'
import { SlackNotificationChannel } from './components/Notifications/SlackWebhook'
import { createRef, createRsrcKey, reverseRsrcKey } from 'features/resources'
import { FullScreenModal, LabelContent, SearchInput } from 'V2Components'
import { ManageAccount } from './components/ManageAccounts'
import { getFriendlyName } from 'Utils/FriendlyName'
import { ServiceNowNotificationChannel } from './components/Notifications/ServiceNow'
import { DeleteIntegrationModal } from 'features/integrations'
import { Endpoints } from '../../const'
import { pushToSlice } from 'infra/redux/sliceHandlers'
import { JiraConfiguration } from './components/JiraConfiguration'
import { createDataSelectorHook } from 'infra/redux'
import { reduxApiClient } from 'infra'
import { MSTeam } from './components/Notifications/MSTeam/MSTeam'
import { MsTeamNotificationWebhook } from './components/Notifications/MsTeamNotificationWebhook'
import { SlackChannels } from './components/ChannelsList/SlackChannels'
import { ServiceNowChannels } from './components/ChannelsList/ServiceNowChannels'
import { MsChannels } from './components/ChannelsList/MsChannels'
import { MsWebhookChannels } from './components/ChannelsList/MsWebhookChannels'
import { JiraChannels } from './components/ChannelsList/JiraChaneels'
import _ from 'lodash'
import { ApiProvider } from 'Core'

const ManageIntegration = () => {
  const history = useHistory()
  const { linkedChannels, refresh, integration } = useChannelBrowser()
  const { kind } = useParams()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const { getObjectRef, selectDispatch } = useMultiSlice([
    'accountList',
    'notificationSubscriptions',
    'servicenowtables',
    'msTeamsChannels'
  ])

  const useSlices = createDataSelectorHook([
    'accountList',
    'notificationSubscriptions',
    'servicenowtables',
    'msTeamsChannels',
    'jiraProjectList' // ensure this slice is included
  ])
  const [showLinkAccountModal, setShowLinkAccountModal] = useState(false)
  const [selectedEntities, setSelectedEntities] = useState([])
  const [currentSelectedChannel, setCurrentSelectedChannel] = useState({})
  const [currentChannelAccounts, setCurrentChannelAccounts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [showJiraConfig, setShowJiraConfig] = useState(false)
  const [issueTypeList, setIssueTypeList] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const { slices } = useSlices()
  const [isConfigLoaded, setIsConfigLoaded] = useState(false)
  const [isDefaultProjectOpened, setIsDefaultProjectOpened] = useState(false)

  // Helper: what type of integration
  const getIntegrationType = () => {
    switch (kind) {
      case 'SlackIntegration':
        return 'SLACK'
      case 'JiraIntegration':
        return 'JIRA'
      case 'ServiceNowIntegration':
        return 'SERVICENOW'
      case 'MsTeamsIntegration':
        return 'TEAMS'
      default:
        return null
    }
  }

  const getChannelAccounts = (channel) => {
    const channelKey = createRsrcKey(channel)
    const accountsKey = []
    slices.notificationSubscriptions.forEach((ns) => {
      const sKey = createRsrcKey(ns.Spec.Subscriber)
      if (channelKey === sKey) {
        accountsKey.push(createRsrcKey(ns.Spec.SubscriptionFor))
      }
    })
    return getObjectRef([...new Set(accountsKey)].map(reverseRsrcKey))
  }

  const getTotalLinkedAccount = (channel) => {
    return getChannelAccounts(channel)
  }

  const getCloudsAccountCount = (channel, type) => {
    const accounts = getChannelAccounts(channel)
    return accounts.filter((account) => account?.Spec?.Type === type)
  }

  const handleEntityModalContinue = async () => {
    try {
      setShowLinkAccountModal(false)
      const currentIds = currentChannelAccounts.map((e) => e.ObjectMeta.ID)
      const newIds = selectedEntities.map((e) => e.ObjectMeta.ID)
      const toRemove = currentIds.filter((id) => !newIds.includes(id))
      const toAdd = newIds.filter((id) => !currentIds.includes(id))

      const toCreate = []
      const toRemoveSubs = []
      toRemove.forEach((accountID) => {
        const slackKey = createRsrcKey(currentSelectedChannel)
        const thisAccKey = `Account+${accountID}`
        const notifObj = slices.notificationSubscriptions.find((ns) => {
          const sKey = createRsrcKey(ns?.Spec?.Subscriber)
          const accKey = createRsrcKey(ns?.Spec?.SubscriptionFor)
          return sKey === slackKey && accKey === thisAccKey
        })
        if (notifObj) {
          toRemoveSubs.push(notifObj)
        }
      })

      toAdd.forEach((accountID) => {
        const obj = {
          SubscriptionFor: { RefKind: 'Account', RefID: accountID },
          Subscriber: createRef(currentSelectedChannel)
        }
        toCreate.push(obj)
      })

      // Create subscriptions
      await Promise.all(
        toCreate.map(async (Spec) => {
          const payload = { ObjectMeta: { Name: getFriendlyName() }, Spec }
          await reduxApiClient('notificationsubscriptions').create(payload)
        })
      )
      // Remove subscriptions
      await Promise.all(
        toRemoveSubs.map(async (ns) => {
          await reduxApiClient('notificationsubscriptions').delete(ns)
        })
      )

      pushToSlice()
      enqueueNotification('Updated channel account linking successfully', 'info')
    } catch (error) {
      console.error('Error in saving owners', error)
      enqueueNotification('Operation Failed', 'error')
    } finally {
      setTimeout(() => {
        setShowLinkAccountModal(false)
      }, 1500)
    }
  }

  const handleManageAccount = (channel) => {
    const accounts = getChannelAccounts(channel)
    setSelectedEntities(accounts)
    setShowLinkAccountModal(true)
    setCurrentSelectedChannel(channel)
    setCurrentChannelAccounts(accounts)
  }

  const handleUnlinkAllAccount = async (channel) => {
    try {
      const accounts = getChannelAccounts(channel)
      const toRemoveSubs = accounts
        .map((account) => {
          const channelKey = createRsrcKey(channel)
          const accKey = createRsrcKey(account)
          return slices.notificationSubscriptions.find((ns) => {
            const sKey = createRsrcKey(ns?.Spec?.Subscriber)
            const subKey = createRsrcKey(ns?.Spec?.SubscriptionFor)
            return sKey === channelKey && subKey === accKey
          })
        })
        .filter(Boolean)

      await Promise.all(
        toRemoveSubs.map((ns) => reduxApiClient('notificationsubscriptions').delete(ns))
      )

      refresh()
      enqueueNotification('Removed all linked channel successfully', 'info')
    } catch (error) {
      console.error('Error in removing all linked channel', error)
      enqueueNotification('Operation Failed', 'error')
    }
  }

  const filteredChannel = linkedChannels.filter((item) => {
    const channelName = item?.Spec?.ChannelName?.toLowerCase()
    const name = item?.Spec?.Name?.toLowerCase()
    const projectName = item?.Spec?.ProjectName?.toLowerCase()
    const term = searchTerm.toLowerCase()

    if (channelName?.includes(term)) return true
    if (!channelName && (name?.includes(term) || projectName?.includes(term))) return true
    return false
  })

  const getChannelName = () => {
    switch (kind) {
      case 'SlackIntegration':
        return integration?.Spec?.WebhookUrl === ''
          ? currentSelectedChannel?.Spec?.ChannelName
          : 'Webhook Channel'
      case 'JiraIntegration':
        return currentSelectedChannel?.Spec?.ProjectName
      case 'ServiceNowIntegration':
        return currentSelectedChannel?.Spec?.Name
      default:
        return ''
    }
  }

  const handleDeleteIntegrationClick = async () => {
    setShowDeleteModal(false)
    try {
      await reduxApiClient(Endpoints[integration?.ObjectMeta?.Kind]).delete(integration)
      enqueueNotification(
        `${integration?.ObjectMeta?.Name} integration removed successfully`,
        'info'
      )
      history.push('/ui/admin/integrations')
    } catch (error) {
      enqueueNotification('Operation failed', 'error')
      console.error(error, 'integration delete error')
    }
  }

  const getHeading = () => {
    switch (kind) {
      case 'SlackIntegration':
        return 'Channel-Account linking'
      case 'JiraIntegration':
        return 'Project-Account linking'
      case 'ServiceNowIntegration':
        return 'Table-Account linking'
      default:
        return ''
    }
  }

  const getKindNotificationComponent = () => {
    switch (kind) {
      case 'SlackIntegration':
        return <SlackNotificationChannel />
      case 'MsTeamsIntegration':
        if (integration?.Spec?.WebhookUrl === '') {
          return <MSTeam />
        } else {
          return <MsTeamNotificationWebhook />
        }
      case 'ServiceNowIntegration':
        return <ServiceNowNotificationChannel />
      default:
        return null
    }
  }

  const getKindChannelsComponent = () => {
    switch (integration?.ObjectMeta?.Kind) {
      case 'SlackIntegration':
        return (
          <SlackChannels
            getCloudsAccountCount={getCloudsAccountCount}
            handleManageAccount={handleManageAccount}
            handleUnlinkAllAccount={handleUnlinkAllAccount}
            getTotalLinkedAccount={getTotalLinkedAccount}
            getIntegrationType={getIntegrationType}
          />
        )
      case 'ServiceNowIntegration':
        return (
          <ServiceNowChannels
            getCloudsAccountCount={getCloudsAccountCount}
            handleManageAccount={handleManageAccount}
            handleUnlinkAllAccount={handleUnlinkAllAccount}
            getTotalLinkedAccount={getTotalLinkedAccount}
            getIntegrationType={getIntegrationType}
            filteredChannel={filteredChannel}
          />
        )
      case 'JiraIntegration':
        return (
          <JiraChannels
            getCloudsAccountCount={getCloudsAccountCount}
            handleManageAccount={handleManageAccount}
            handleUnlinkAllAccount={handleUnlinkAllAccount}
            getTotalLinkedAccount={getTotalLinkedAccount}
            getIntegrationType={getIntegrationType}
            filteredChannel={filteredChannel}
            handleConfigProject={handleConfigProject}
          />
        )
      case 'MsTeamsIntegration':
        if (integration?.Spec?.WebhookUrl === '') {
          return (
            <MsChannels
              getCloudsAccountCount={getCloudsAccountCount}
              handleManageAccount={handleManageAccount}
              handleUnlinkAllAccount={handleUnlinkAllAccount}
              getTotalLinkedAccount={getTotalLinkedAccount}
              filteredChannel={filteredChannel}
              msTeamsChannels={slices.msTeamsChannels}
            />
          )
        } else {
          return (
            <MsWebhookChannels
              getCloudsAccountCount={getCloudsAccountCount}
              handleManageAccount={handleManageAccount}
              handleUnlinkAllAccount={handleUnlinkAllAccount}
              getTotalLinkedAccount={getTotalLinkedAccount}
            />
          )
        }
      default:
        return null
    }
  }

  const handleConfigProject = async (account) => {
    selectDispatch(['jiraProjectList'])
    try {
      const response = await reduxApiClient('jiraissue').getByDataParams({
        JiraProjectKey: account.Spec.ProjectKey,
        JiraIntegrationUUID: integration.ObjectMeta.UUID
      })
      if (response) {
        setSelectedProject(account)
        setIssueTypeList(response)
        // setShowJiraConfig(true)
        setIsDefaultProjectOpened(true)
      } else {
        console.warn('No jira issue config returned')
      }
    } catch (e) {
      console.error('jiraissue fetch error:', e)
    }
  }

  // effect
  useEffect(() => {
    if (kind === 'JiraIntegration' && !isDefaultProjectOpened && linkedChannels.length > 0) {
      handleConfigProject(linkedChannels[0])
    }
  }, [linkedChannels, isDefaultProjectOpened, kind])

  const handleSaveJiraConfig = async (configData) => {
    try {
      const { issueType, rows, stateMapping, project } = configData

      // Filter the existing project item
      const filterCurrentJira = slices.jiraProjectList.filter(
        (jira) => jira.ObjectMeta.ID === project.ObjectMeta.ID
      )
      if (!filterCurrentJira.length) {
        throw new Error('Selected project config not found')
      }

      const payload = _.cloneDeep(filterCurrentJira[0])
      payload.Spec.JiraIssueType = issueType
      payload.Spec.StatusMapping.Map = { ...stateMapping }

      if (rows?.length >= 1) {
        payload.Spec.Attributes.Map = {}
        rows.forEach((item) => {
          payload.Spec.Attributes.Map[item.fieldName] = `{{${item.resourceName}}}`
        })
      } else {
        payload.Spec.Attributes.Map = {}
      }
      console.log(payload)
      const response = await reduxApiClient('jiraprojects').update(payload)
      pushToSlice(response)
      await reduxApiClient('jiraprojects').getAll({})
      enqueueNotification('Jira config updated successfully!', 'info')
    } catch (error) {
      enqueueNotification('Error updating Jira config', 'error')
      console.error(error)
    }
  }

  return (
    <>
      <Header
        integration={integration}
        setShowDeleteModal={setShowDeleteModal}
        isJiraConfigEditable={false}
      />
      <div className='bg-[var(--color-common-bg-base)] p-4 min-h-screen'>
        {['SlackIntegration', 'MsTeamsIntegration'].includes(kind) && (
          <div className='flex mt-2 pr-4 w-[100%]'>
            <LabelContent title='Webhook URL' content={integration.Spec.WebhookUrl} />
          </div>
        )}

        <div className='flex justify-between'>
          {kind === 'JiraIntegration' ? (
            <>
              <div className='w-1/2 border-r border[#D8DDE4] pr-8'>
                <div>
                  <div className='flex justify-between mt-5'>
                    <Typography variant='h3'>{getHeading()}</Typography>
                    <SearchInput searchKey={searchTerm} onChange={setSearchTerm} />
                  </div>
                  <div className='mt-8'>{getKindChannelsComponent()}</div>
                </div>
              </div>
              <div className='w-1/2 pl-8'>
                {/* {showJiraConfig && selectedProject ? (
                  <JiraConfiguration
                    key={selectedProject.Spec.ProjectName}
                    issueTypeList={issueTypeList}
                    selectedProject={selectedProject}
                    onSave={handleSaveJiraConfig}
                  />
                ) : (
                  <div className='py-10 text-center'>Loading project config…</div>
                )} */}
              </div>
            </>
          ) : (
            <>
              <div className='w-1/2 border-r border[#D8DDE4] pr-8'>
                <Typography variant='h3' className='mt-5'>
                  Notification
                </Typography>
                {getKindNotificationComponent()}
              </div>
              <div className='w-1/2 pl-8'>
                <div className='flex justify-between mt-5'>
                  <Typography variant='h3'>{getHeading()}</Typography>
                  <SearchInput searchKey={searchTerm} onChange={setSearchTerm} />
                </div>
                <div className='mt-8 h-[71vh] overflow-auto'>{getKindChannelsComponent()}</div>
              </div>
            </>
          )}
        </div>

        {showLinkAccountModal && (
          <FullScreenModal showModal={showLinkAccountModal}>
            <div className='flex justify-center items-center h-[100%] bg-[#2229455a]'>
              <div className='rounded-lg p-8 bg-white'>
                <ManageAccount
                  title={`Link accounts to ${getChannelName()} channel`}
                  errorMessage=''
                  entities={slices.accountList}
                  selectedEntities={selectedEntities}
                  setSelectedEntities={setSelectedEntities}
                  onCancel={() => setShowLinkAccountModal(false)}
                  onContinue={handleEntityModalContinue}
                />
              </div>
            </div>
          </FullScreenModal>
        )}

        {showDeleteModal && (
          <DeleteIntegrationModal
            integrationName={integration}
            showDeleteModal={showDeleteModal}
            setShowDeleteModal={setShowDeleteModal}
            handleDeleteIntegrationClick={handleDeleteIntegrationClick}
          />
        )}
      </div>
    </>
  )
}

const ManageIntegrationsWrapper = () => (
  <ChannelBrowserContext>
    <ManageIntegration />
  </ChannelBrowserContext>
)

export default ManageIntegrationsWrapper
